import {
  AdvancedSearchOptions,
  AdvancedSearchType,
  AdvancedSearchTypeEnum,
  Operator,
  ReloadSearchResultResponse,
  SearchRequest,
  SearchResponse,
  searchResult,
  SearchResultRequest,
  SearchTypeEnum,
  SessionSearchItem,
  ISaveRequest,
} from '../../../api/types/sessionSearch.type';
import {Query, QueryType} from '../types/Query';
import {SessionSearch} from '../types/SessionSearch';

export const hasAnyCriteriaAdded = (item: SessionSearch): boolean => {
  if (!item.query || item.query.length === 0) {
    return false;
  }

  return item.query.some((q) =>
    q.value?.some(
      (c) => c.value !== undefined && c.value !== null && c.value !== ''
    )
  );
};

export function getCriteriaValues(query: Query): string {
  if (!query || !query.value || query.value.length === 0) {
    return '';
  }

  return query.value
    .map((c) =>
      normalizeBooleanFlags(
        c.value !== undefined && c.value !== null ? String(c.value) : ''
      )
    )
    .filter((v) => v.trim() !== '')
    .join(' ');
}

// Helper to convert QueryType to AdvancedSearchTypeEnum
function mapQueryTypeToEnum(type: QueryType): AdvancedSearchTypeEnum {
  switch (type) {
    case QueryType.Analytics:
      return 'ANALYTICS';
    case QueryType.WorkProduct:
      return 'WORKPRODUCT';
    case QueryType.Conceptual:
      return 'CONCEPTUAL';
    case QueryType.ContentAndMetadata:
      return 'CONTENTMETADATA';
    default:
      throw new Error(`Unsupported QueryType: ${type}`);
  }
}

const normalize = (val: any): string => {
  if (val === undefined || val === null) return '';

  let v = String(val);

  // Normalize curly quotes to straight quotes
  v = v.replace(/“|”/g, '"');

  //v = v.replace(/"/g, '\\"');

  return v;
};

const normalizeBooleanFlags = (val: string) => {
  if (/AudioPlayerReady|VideoPlayerReady/.test(val)) {
    return val
      .replace(/\(\s*true\s*\)/i, '(1)')
      .replace(/\(\s*false\s*\)/i, '(0)');
  }
  return val;
};

// Helper to build criteria string from query values
export function buildCriteriaString(query: Query): string {
  if (!query.value || query.value.length === 0) return '';
  const cleaned = query.value
    .map((c) => normalizeBooleanFlags(normalize(c.value)))
    .filter((v) => v.trim() !== '');

  return cleaned.join(' ');
}

export function buildDBCriteriaString(query: Query): string {
  if (!query.value || query.value.length === 0) return '';

  const cleaned = query.value
    .map((c) => normalize(c.value))
    .filter((v) => v.trim() !== '');

  return query.type === QueryType.ContentAndMetadata
    ? cleaned.join('`')
    : cleaned.join(' ');
}

function buildAdvancedSearchOptions(
  session: SessionSearch
): AdvancedSearchOptions {
  return {
    includeSanitationFilter: session.regexOnWorkProductAttributes,
    includeEmailDocuments: session.threadedEmailDocuments,
    includeFamilyMember: session.familyMemberDocuments,
    includeNearDupe: session.nearDuplicateDocuments,
  };
}

function getconceptScore(q: Query): number {
  if (q.type == QueryType.Conceptual) {
    if (q.value?.length > 0) {
      return q.value[0].conceptScore ? q.value[0].conceptScore : 0;
    } else {
      return 0;
    }
  } else {
    return 0;
  }
}

function buildAdvancedSearchTypes(queries?: Query[]): AdvancedSearchType[] {
  if (!queries || queries.length === 0) return [];

  return (
    queries.map((q, idx) => ({
      sequenceNo: idx + 1,
      operator: q.operator as Operator,
      criteriaDB: replaceEscapedQuotes(buildDBCriteriaString(q)),
      criteriaBackground: buildCriteriaString(q),
      caatConceptScore: getconceptScore(q),
      audioOperator: '',
      audioLanguage: '',
      audioFileLocation: '',
      audioThreshold: 0,
      timeBasisMS: 0,
      advancedSearchType: mapQueryTypeToEnum(q.type),
      persistentSearchHitTerm: '',
      highlightRegex: [],
    })) ?? []
  );
}

export function mapSessionToSessionSearchItem(
  session: SessionSearch
): SessionSearchItem {
  return {
    searchLabel: session.name,
    createdBy: 2,
    searchBatchID: 0,
    searchSessionId: session.id,
    projectId: 0,
    searchType: 'ADVANCED',
    isPinned: Boolean(session.isPinned),
    searchID: 0,
    editSavedSearchID: 0,
    originalSearchQuery: '',
    searchTitle: session.name,
    searchStatus: '',
    draggedTiles: [],
    advancedSearchOptions: buildAdvancedSearchOptions(session),
    advancedSearchTypes: buildAdvancedSearchTypes(session.query),
    searchLabelText: session.name,
    retainConfidenceScore: true,
  };
}

// Main utility function
export function createSearchRequest(session: SessionSearch): SearchRequest {
  const sessionItem = mapSessionToSessionSearchItem(session);
  const advancedSearchOptions: AdvancedSearchOptions = {
    includeSanitationFilter: session.regexOnWorkProductAttributes,
    includeEmailDocuments: session.threadedEmailDocuments,
    includeFamilyMember: session.familyMemberDocuments,
    includeNearDupe: session.nearDuplicateDocuments,
  };

  const advancedSearchTypes: AdvancedSearchType[] = buildAdvancedSearchTypes(
    session.query
  );

  const request: SearchRequest = {
    projectID: 0,
    projectGUID: '00000000-0000-0000-0000-000000000000', //this is not used on the server,server uses userContext.projectGUID
    audioIndexGuid: '',
    securityGroupID: 0,
    advancedSearchTypes,
    searchType: 'ADVANCED' as SearchTypeEnum,
    advSessionSearchID: session.id,
    advancedSearchOptions,
    advSearchID: 0,
    sessionID: session.id,
    sessionSearchList: [sessionItem],
  };

  return request;
}

function replaceEscapedQuotes(input: string): string {
  return input.replace(/"/g, '&quot;');
}

export function createSearchResultRequest(
  searchResponse: SearchResponse,
  overrides?: Partial<
    Omit<SearchResultRequest, 'sessionSearchList' | 'searchId'>
  >
): SearchResultRequest {
  searchResponse.sessionSearchList.pop();
  return {
    searchId: searchResponse.advancedSearchId,
    resultType: overrides?.resultType ?? 'ADVANCEDSEARCH',
    isElasticSearch: overrides?.isElasticSearch ?? 0,
    isSavedSearch: overrides?.isSavedSearch ?? false,
    securityGroup: overrides?.securityGroup ?? 0, //not used , server uses userContext.SecurityGroupID,
    sessionSearchList: searchResponse.sessionSearchList,
    sessionSearchId:
      overrides?.sessionSearchId ??
      (searchResponse.sessionSearchList.length > 0
        ? searchResponse.sessionSearchList[0].searchSessionId
        : ''),
  };
}

// Utility function to convert ReloadSearchResultResponse -> searchResult
export function toSearchResult(
  response: ReloadSearchResultResponse
): searchResult {
  return {
    pureHitStatus: response.pureHitStatus,
    emailThreadStatus: response.emailThreadStatus,
    familyMemberStatus: response.familyMemberStatus,
    nearDuplicateStatus: response.nearDuplicateStatus,
    conceptualStatus: response.conceptualStatus,
    pureHit: {
      pureHitCount: response.pureHitCount,
      pureHitData: response.pureHitData,
      addedToShoppingCart: false,
    },
    emailThread: {
      emailThreadCount: response.emailThreadCount,
      emailThreadData: response.emailThreadData,
      addedToShoppingCart: false,
    },
    familyMember: {
      familyMemberCount: response.familyMemberCount,
      familyMemberData: response.familyMemberData,
      addedToShoppingCart: false,
    },
    nearDuplicate: {
      nearDuplicateCount: response.nearDuplicateCount,
      nearDuplicateData: response.nearDuplicateData,
      addedToShoppingCart: false,
    },
  };
}

export const createEmptySearchResult = (): searchResult => {
  return {
    pureHitStatus: '',
    emailThreadStatus: '',
    familyMemberStatus: '',
    nearDuplicateStatus: '',
    conceptualStatus: '',
    ConceptualResultInShoppingCart: false,

    pureHit: {
      pureHitCount: '0',
      pureHitData: [],
      addedToShoppingCart: false,
    },

    emailThread: {
      emailThreadCount: '0',
      emailThreadData: [],
      addedToShoppingCart: false,
    },

    familyMember: {
      familyMemberCount: '0',
      familyMemberData: [],
      addedToShoppingCart: false,
    },

    nearDuplicate: {
      nearDuplicateCount: '0',
      nearDuplicateData: [],
      addedToShoppingCart: false,
    },
  };
};

export const parseDate = (value: string | Date): Date =>
  value instanceof Date ? value : new Date(value);

export const isExecuted = (item: SessionSearch) =>
  item.lastRuntime || item.advancedSearchId;

export const hasConceptualSearchCriteria = (item: SessionSearch) => {
  if (!item.query) {
    return false;
  }
  if (item.query.length === 0) {
    return false;
  }

  const hasAnyConceptualCriteria = item.query.some(
    (i) => i.type === QueryType.Conceptual
  );
  return hasAnyConceptualCriteria;
};

export function createSaveRequest(
  session: SessionSearch,
  searchName: string,
  retainConfidenceScore: boolean,
  savedSearchID: number = 0,
  destinationSavedSearchID: number = 0,
  searchGroupID: number = 0,
  securityGroupID: number = 0
): ISaveRequest {
  const advancedSearchOptions: AdvancedSearchOptions = {
    includeSanitationFilter: session.regexOnWorkProductAttributes,
    includeEmailDocuments: session.threadedEmailDocuments,
    includeFamilyMember: session.familyMemberDocuments,
    includeNearDupe: session.nearDuplicateDocuments,
  };

  const advancedSearchTypes: AdvancedSearchType[] = buildAdvancedSearchTypes(
    session.query
  );

  const request: ISaveRequest = {
    // Default values
    sessionID: session.id,
    advSessionSearchID: session.id,
    searchType: 'ADVANCED' as SearchTypeEnum,

    // Input values
    searchLabel: searchName,
    searchGroupID: searchGroupID,
    retainConfidenceScore: retainConfidenceScore,
    searchID: destinationSavedSearchID, // saved search to overwrite
    sessionSearchID: savedSearchID, // currently selected saved search
    selectedSecurityGropuId: securityGroupID,

    // Not required
    projectID: 0,
    projectGUID: '00000000-0000-0000-0000-000000000000',
    securityGroupID: 0,

    advancedSearchOptions,
    advancedSearchTypes,
    sessionSearchList: [],
  };

  return request;
}

export function toSearchResultRequest(
  sessionSearch: SessionSearch,
  options?: {
    searchId?: number;
    resultType?: string;
    isElasticSearch?: number;
    isSavedSearch?: boolean;
    securityGroup?: number;
    createdBy?: number;
    projectId?: number;
  }
): SearchResultRequest {
  const {
    searchId = sessionSearch.advancedSearchId ?? 0,
    resultType = 'ADVANCED',
    isElasticSearch = 0,
    isSavedSearch = !!sessionSearch.isSaved,
    securityGroup = 0,
    createdBy = 0,
    projectId = 0,
  } = options || {};

  const sessionSearchItem: SessionSearchItem = {
    searchLabel: sessionSearch.name,
    createdBy,
    searchBatchID: 0,
    searchSessionId: sessionSearch.id,
    projectId,
    searchType: 'ADVANCED',
    isPinned: !!sessionSearch.isPinned,
    searchID: searchId,
    editSavedSearchID: 0,
    originalSearchQuery: JSON.stringify(sessionSearch.query ?? []),
    searchTitle: sessionSearch.name,
    searchStatus: sessionSearch.searchIsInProgress ? 'IN_PROGRESS' : 'READY',
    draggedTiles: [],
    advancedSearchOptions: {
      includeSanitationFilter: true,
      includeEmailDocuments: sessionSearch.threadedEmailDocuments,
      includeFamilyMember: sessionSearch.familyMemberDocuments,
      includeNearDupe: sessionSearch.nearDuplicateDocuments,
    },
    advancedSearchTypes: buildAdvancedSearchTypes(sessionSearch.query),
    searchLabelText: sessionSearch.name,
    retainConfidenceScore: false,
  };

  return {
    searchId,
    resultType,
    isElasticSearch,
    isSavedSearch,
    securityGroup,
    sessionSearchList: [sessionSearchItem],
    sessionSearchId: sessionSearch.id,
  };
}
